import DropdownSelect from "#components/DropdownSelect/index.js";
import TranslatableTextField from "#components/TranslatableTextField/index.js";
import { Grid, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LoadingButton from "components/LoadingButton";
import Dialog from "components/NoScrollDialog";
import { useLanguage } from "contexts/language.context";
import { formatDateTimeFromISOStringUTC } from "layouts/audits/components/utils";
import * as React from "react";
import { useEffect } from "react";
import branchesService from "services/branches-service";
import sheetTemplatesServices from "services/sheet-templates-services";
import usersService from "services/users-service";

export default function SheetsDialog({
  isOpen,
  onSubmit,
  data,
  mode,
  onClose,
}) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [updates, setUpdates] = React.useState({});
  const [initialSheet, setInitialSheet] = React.useState({
    name: { en: "" },
    referenceCode: "",
  });
  const [options, setOptions] = React.useState({ sheetTemplates: [] });
  const { label } = useLanguage();
  const [error, setError] = React.useState(null);

  const sheet = React.useMemo(
    () => ({
      ...{
        ...initialSheet,
      },
      ...updates,
    }),
    [initialSheet, updates],
  );

  useEffect(() => {
    setUpdates({});
  }, [initialSheet?.id]);

  async function fetch() {
    let [ok, data] = await sheetTemplatesServices.getTemplates();
    if (ok) {
      setOptions((options) => ({ ...options, sheetTemplates: data }));
    }

    [ok, data] = await branchesService.getBranches({ raw: true });
    if (ok) {
      setOptions((options) => ({ ...options, branches: data }));
    }

    [ok, data] = await usersService.getUsers({ raw: true, view: "minimal" });
    if (ok) {
      setOptions((options) => ({ ...options, users: data }));
    }
  }

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setLoading(false);
    setInitialSheet({
      name: { en: "" },
    });
  }, [isOpen, mode]);

  React.useEffect(() => {
    if (data) setInitialSheet({ ...data });
  }, [data, mode, isOpen]);

  React.useEffect(() => {
    setError(null);
    if (isOpen) fetch();
  }, [mode, isOpen]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const isScheduled = React.useMemo(() => {
    return !!data?.scheduledFor;
  }, [data?.scheduledFor]);

  const dropdownOptions = React.useMemo(() => {
    const branch = options.branches?.find(
      (branch) => branch.id == sheet.branchId,
    );
    const sheetTemplate = options.sheetTemplates?.find(
      (sheetTemplate) => sheetTemplate.id == sheet.sheetTemplateId,
    );

    if (!isScheduled) {
      return {
        sheetTemplates:
          options.sheetTemplates
            ?.filter((sheetTemplate) =>
              branch && sheetTemplate?.accountId
                ? branch.branchGroup.accountId == sheetTemplate?.accountId
                : true,
            )
            ?.filter((sheetTemplate) =>
              branch && sheetTemplate?.branchGroupId
                ? branch.branchGroupId == sheetTemplate?.branchGroupId
                : true,
            )
            ?.filter((sheetTemplate) =>
              branch && sheetTemplate?.branchId
                ? branch.id == sheetTemplate?.branchId
                : true,
            )

            ?.map((v) => ({ id: v.id, label: v.name })) || [],
        branches:
          options.branches?.map((v) => ({
            id: v.id,
            label: v.name,
            branchGroup: v.branchGroup?.name,
          })) || [],

        users: options.users
          ?.filter((user) =>
            branch ? branch?.branchGroup?.accountId == user?.accountId : true,
          )
          .map((v) => ({
            id: v.id,
            label: v.name,
            account: v.account?.name,
          })),
      };
    } else {
      return {
        sheetTemplates:
          options.sheetTemplates?.map((v) => ({ id: v.id, label: v.name })) ||
          [],
        branches:
          options.branches
            ?.filter((b) =>
              sheetTemplate && sheetTemplate?.accountId
                ? b.branchGroup.accountId == sheetTemplate?.accountId
                : true,
            )
            ?.filter((b) =>
              sheetTemplate && sheetTemplate?.branchGroupId
                ? b.branchGroupId == sheetTemplate?.branchGroupId
                : true,
            )
            ?.filter((b) =>
              sheetTemplate && sheetTemplate?.branchId
                ? b.id == sheetTemplate?.branchId
                : true,
            )
            .map((v) => ({
              id: v.id,
              label: v.name,
              branchGroup: v.branchGroup?.name,
            })) || [],
        users: options.users
          ?.filter((user) =>
            sheetTemplate && sheetTemplate?.accountId
              ? user.accountId == sheetTemplate?.accountId
              : true,
          )
          ?.map((v) => ({
            id: v.id,
            label: v.name,
            account: v.account?.name,
          })),
      };
    }
  }, [options, sheet.branchId, isScheduled, sheet.sheetTemplateId]);

  useEffect(() => {
    if (sheet.sheetTemplateId) {
      const sheetTemplate = options.sheetTemplates.find(
        (v) => v.id == sheet.sheetTemplateId,
      );
      if (sheetTemplate?.userId) {
        setUpdates((u) => ({ ...u, userId: sheetTemplate?.userId }));
      }
    }
  }, [sheet.sheetTemplateId, options.sheetTemplates]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          const result = await onSubmit(sheet);

          if (result.error) {
            setError(result.error);
          } else {
            setError(null);
            handleClose();
          }

          setLoading(false);
        },
      }}
    >
      <DialogTitle>
        {mode == "edit" ? label("edit-sheet") : label("create-sheet")}
        {mode == "edit"
          ? ""
          : data?.scheduledFor
            ? " scheduled for " +
              formatDateTimeFromISOStringUTC(data?.scheduledFor)
            : ""}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1.5 }}>
          <Grid item xs={12}>
            <TranslatableTextField
              autoFocus
              required
              id="name"
              name="name"
              label={label("name-singular")}
              type="text"
              values={sheet.name}
              onChange={(e) =>
                setUpdates((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <DropdownSelect
              required
              label={"Branches"}
              initialValue={sheet.branchId}
              items={dropdownOptions.branches}
              onChange={(value) => {
                setUpdates((prev) => ({
                  ...prev,
                  branchId: value?.id || "",
                }));
              }}
              disabled={!!sheet?.sheetTemplate?.branchId}
              groupBy={(option) => option.branchGroup}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <DropdownSelect
              disabled={isScheduled}
              required
              label={"Templates"}
              initialValue={sheet.sheetTemplateId}
              items={dropdownOptions.sheetTemplates}
              onChange={(value) => {
                setUpdates((prev) => ({
                  ...prev,
                  sheetTemplateId: value?.id || "",
                }));
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <DropdownSelect
              required
              label={"Assigned to"}
              initialValue={sheet.userId}
              items={dropdownOptions.users}
              onChange={(value) => {
                setUpdates((prev) => ({
                  ...prev,
                  userId: value?.id || "",
                }));
              }}
              groupBy={(option) => option.account}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {mode == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
